<template>
  <v-dialog :value="value" max-width="1200" persistent @input="$emit('input', $event)">
    <v-card :loader-height="2">
      <v-card-text class="tableHeaderBg d-flex align-center py-2 px-4">
        <span class="font-size-md">File Config View</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="close" class="ml-2" data-testid="config-view-dialog-v2__close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-text>

      <v-card-text class="config-view-v2">
        <div class="font-size-md mb-2 font-weight-bold">Configuration Summary</div>

        <!-- ─── 1. Basic Details ─── -->
        <div class="config-view-v2__section">
          <div class="config-view-v2__section-title">Basic Details</div>
          <div class="config-view-v2__detail">
            File Name: <span>{{ config.file }}</span>
          </div>
          <div class="config-view-v2__detail">
            Version: <span>{{ config.version || 'v2' }}</span>
          </div>
        </div>

        <!-- ─── 2. File Types ─── -->
        <div class="config-view-v2__section" v-if="fileTypes.length > 0">
          <div class="config-view-v2__section-title">File Types</div>
          <v-simple-table dense class="config-view-v2__file-types-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Key</th>
                <th>Display Name</th>
                <th>Ignore Rows</th>
                <th>Ignore Cols</th>
                <th>Footer Rows</th>
                <th>Blank Headers</th>
                <th>Keep Dupes</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(ft, idx) in fileTypes"
                :key="'ft-' + idx"
                data-testid="config-view-dialog-v2__file-type-row"
              >
                <td>{{ idx + 1 }}</td>
                <td><code>{{ ft.key }}</code></td>
                <td>{{ ft.name }}</td>
                <td>{{ ft.ignorerows || 0 }}</td>
                <td>{{ ft.ignorecolumns || 0 }}</td>
                <td>{{ ft.ignorefooterrows || 0 }}</td>
                <td>{{ ft.blankcolumnheaders || '—' }}</td>
                <td>{{ ft.keepduplicatecolumns ? 'Yes' : 'No' }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </div>

        <!-- ─── 3. Column Mapping ─── -->
        <div class="config-view-v2__section" v-if="columnmap.length > 0">
          <div class="config-view-v2__section-title">Column Mapping</div>
          <div class="config-view-v2__mapping-table-wrapper">
            <v-simple-table dense class="config-view-v2__mapping-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Base Column</th>
                  <th>Type</th>
                  <th>User Column Mapping</th>
                  <th
                    v-for="ft in fileTypes"
                    :key="'th-' + ft.key"
                  >
                    {{ ft.name }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(col, idx) in columnmap"
                  :key="'cm-' + idx"
                  data-testid="config-view-dialog-v2__mapping-row"
                >
                  <td>{{ idx + 1 }}</td>
                  <td class="font-weight-medium">{{ col.label }}</td>
                  <td>{{ col.type }}</td>
                  <td>{{ col.userColumnMappingLabel || nullDisplay }}</td>
                  <td
                    v-for="ft in fileTypes"
                    :key="'td-' + ft.key + '-' + idx"
                  >
                    <span
                      :class="{
                        'config-view-v2__null-cell': !getAliasHeader(col, ft.key)
                      }"
                    >
                      {{ getAliasHeader(col, ft.key) || nullDisplay }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </div>

        <!-- ─── 4. Primary Keys ─── -->
        <div class="config-view-v2__section" v-if="primaryKeys.length > 0">
          <div class="config-view-v2__section-title">Primary Keys</div>
          <div class="d-flex flex-wrap" style="gap: var(--space-1);">
            <v-chip
              v-for="(pk, idx) in primaryKeys"
              :key="'pk-' + idx"
              label
              small
              color="primary"
              outlined
            >
              <span class="font-weight-medium mr-1">{{ idx + 1 }}.</span> {{ pk }}
            </v-chip>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'ConfigViewDialogV2',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      nullDisplay: '—',
    };
  },
  computed: {
    fileTypes() {
      return this.config.fileTypes || [];
    },
    columnmap() {
      return this.config.columnmap || [];
    },
    primaryKeys() {
      return this.config.primaryKeys || [];
    },
  },
  methods: {
    close() {
      this.$emit('input', false);
    },
    getAliasHeader(col, fileTypeKey) {
      const alias = col.fileTypeAliases && col.fileTypeAliases[fileTypeKey];
      if (!alias || alias.header === null || alias.header === undefined) {
        return null;
      }
      return alias.header;
    },
  },
};
</script>

<style lang="scss" scoped>
@import './ConfigViewDialogV2.scss';
</style>
